import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  signUpEventUser,
  getUsersByEventId,
  updateUser,
} from "../../server/Api";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import toast from "react-hot-toast";
import { EVENT_ROLES } from "../../utils/eventRoles";
import {
  PAGE_SPECIAL_ACTIONS,
  USER_ACCESS_MODULES,
  buildDefaultPagePermissions,
} from "../../utils/pagePermissions";
import { groupAccessModules } from "../../utils/eventAccessModules";
import "../../assets/css/occasion.css";
import "../../assets/css/EventUser.css";

const emptyForm = {
  name: "",
  email: "",
  password: "",
  role: "Viewer",
};

const EventUser = () => {
  const user = useSelector((state) => state.auth.user);
  const selectedEvent = useSelector((state) => state.auth.selectedEvent);

  const [users, setUsers] = useState([]);
  const [eventAllowedPages, setEventAllowedPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [draftRole, setDraftRole] = useState("Viewer");
  const [draftPermissions, setDraftPermissions] = useState([]);
  const [confirmation, setConfirmation] = useState(null);

  const canManageUsers = Boolean(
    user?.isSuperAdmin || user?.isAdmin || selectedEvent?.isAdmin
  );

  const fetchUsers = useCallback(async () => {
    if (!selectedEvent?.eventId || !canManageUsers) return;
    setLoading(true);
    try {
      const response = await axios.get(
        getUsersByEventId(selectedEvent.eventId)
      );
      const payload = response.data;
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.users)
          ? payload.users
          : [];
      const allowed = Array.isArray(payload?.eventAllowedPages)
        ? payload.eventAllowedPages
        : selectedEvent.eventAllowedPages ||
          selectedEvent.allowedPages ||
          USER_ACCESS_MODULES.map((m) => m.key);

      setUsers(list);
      setEventAllowedPages(allowed);
      setSelectedUserId((prev) => {
        if (prev && list.some((u) => String(u._id) === String(prev))) return prev;
        return list[0]?._id || null;
      });
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error(
        error.response?.data?.message || "Failed to load team members."
      );
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [selectedEvent?.eventId, canManageUsers, selectedEvent?.allowedPages, selectedEvent?.eventAllowedPages]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const selectedUser = useMemo(
    () => users.find((u) => String(u._id) === String(selectedUserId)) || null,
    [users, selectedUserId]
  );

  useEffect(() => {
    if (!selectedUser) {
      setDraftRole("Viewer");
      setDraftPermissions(
        buildDefaultPagePermissions(eventAllowedPages, "Viewer")
      );
      return;
    }
    setDraftRole(selectedUser.role || "Viewer");
    setDraftPermissions(
      Array.isArray(selectedUser.pagePermissions) &&
        selectedUser.pagePermissions.length
        ? selectedUser.pagePermissions.map((p) => ({ ...p }))
        : buildDefaultPagePermissions(
            eventAllowedPages,
            selectedUser.role || "Viewer"
          )
    );
  }, [selectedUser, eventAllowedPages]);

  const permissionGroups = useMemo(() => {
    const allowed = new Set(eventAllowedPages);
    const modules = USER_ACCESS_MODULES.filter((m) => allowed.has(m.key)).map(
      (m) => {
        const perm =
          draftPermissions.find((p) => p.pageKey === m.key) || {
            pageKey: m.key,
            view: false,
            edit: false,
            delete: false,
            specialActions: false,
          };
        return { ...m, perm };
      }
    );
    return groupAccessModules(modules);
  }, [eventAllowedPages, draftPermissions]);

  const filteredUsers = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (row) =>
        String(row.name || "")
          .toLowerCase()
          .includes(q) ||
        String(row.email || "")
          .toLowerCase()
          .includes(q)
    );
  }, [users, searchTerm]);

  const togglePerm = (pageKey, flag) => {
    if (pageKey === "dashboard" && flag !== "view") return;
    setDraftPermissions((prev) => {
      const next = [...prev];
      let idx = next.findIndex((p) => p.pageKey === pageKey);
      if (idx < 0) {
        next.push({
          pageKey,
          view: false,
          edit: false,
          delete: false,
          specialActions: false,
        });
        idx = next.length - 1;
      }
      const row = { ...next[idx] };
      if (flag === "view") {
        row.view = !row.view;
        if (!row.view) {
          row.edit = false;
          row.delete = false;
          row.specialActions = false;
        }
      } else {
        if (!row.view) return prev;
        if (flag === "specialActions" && !PAGE_SPECIAL_ACTIONS[pageKey]) {
          return prev;
        }
        row[flag] = !row[flag];
      }
      next[idx] = row;
      return next;
    });
  };

  const applyRolePreset = (role) => {
    setDraftRole(role);
    setDraftPermissions(buildDefaultPagePermissions(eventAllowedPages, role));
  };

  const handleSaveAccess = async () => {
    if (!selectedUser || !selectedEvent?.eventId) {
      toast.error("Select a team member first");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(updateUser(selectedUser._id), {
        eventId: selectedEvent.eventId,
        role: draftRole,
        pagePermissions: draftPermissions,
      });
      const updated = res.data?.user;
      setUsers((prev) =>
        prev.map((u) =>
          String(u._id) === String(selectedUser._id)
            ? updated || {
                ...u,
                role: draftRole,
                pagePermissions: draftPermissions,
              }
            : u
        )
      );
      toast.success("User access saved");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to save user access"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!selectedEvent?.eventId) {
      toast.error("No event selected");
      return;
    }
    if (!formData.name.trim() || !formData.email.trim() || !formData.password) {
      toast.error("Name, email, and password are required");
      return;
    }
    if (formData.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setSaving(true);
    try {
      const matrix = buildDefaultPagePermissions(
        eventAllowedPages,
        formData.role
      );
      await axios.post(signUpEventUser(), {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        role: formData.role,
        eventId: selectedEvent.eventId,
        pagePermissions: matrix,
        enteredBy: user?.name || user?.email || "Event Admin",
      });
      toast.success("Team member added");
      setFormData(emptyForm);
      setShowAddForm(false);
      await fetchUsers();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to add team member"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleActivateDeactivate = (row) => {
    setConfirmation({
      userId: row._id,
      userName: row.name || row.email,
      newStatus: !row.isActive,
      message: `Are you sure you want to ${
        !row.isActive ? "enable" : "disable"
      } ${row.name || row.email}?`,
    });
  };

  const confirmActivateDeactivate = async (confirmed) => {
    if (!confirmed || !confirmation) {
      setConfirmation(null);
      return;
    }
    try {
      await axios.put(updateUser(confirmation.userId), {
        eventId: selectedEvent.eventId,
        isActive: confirmation.newStatus,
      });
      toast.success(
        `User ${confirmation.newStatus ? "enabled" : "disabled"} successfully`
      );
      await fetchUsers();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to update user status."
      );
    }
    setConfirmation(null);
  };

  if (!canManageUsers) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="container-fluid event-user-page">
      <div className="occasion-top row g-4 align-items-center">
        <div className="col-lg-5">
          <h2 className="mb-0">User Access</h2>
        </div>
        <div className="col-lg-7">
          <div className="event-user-top-actions">
            <input
              type="text"
              className="form-control"
              placeholder="Search name or email…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {showAddForm && (
        <form className="event-user-add-form" onSubmit={handleAddUser}>
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label">
                Name <span className="required">*</span>
              </label>
              <input
                className="form-control custom-form-control"
                name="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, name: e.target.value }))
                }
                required
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">
                Email <span className="required">*</span>
              </label>
              <input
                type="email"
                className="form-control custom-form-control"
                name="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, email: e.target.value }))
                }
                required
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">
                Password <span className="required">*</span>
              </label>
              <input
                type="password"
                className="form-control custom-form-control"
                name="password"
                value={formData.password}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, password: e.target.value }))
                }
                required
                minLength={6}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Role preset</label>
              <select
                className="form-select"
                value={formData.role}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, role: e.target.value }))
                }
              >
                {EVENT_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <button
                type="submit"
                className="btn btn-save event-user-action-btn w-100"
                disabled={saving}
              >
                {saving ? "Saving…" : "Create"}
              </button>
            </div>
          </div>
        </form>
      )}

      <div className="event-user-layout">
        <aside className="event-user-member-list" aria-label="Team members">
          <div className="event-user-member-list__head">
            <h3 className="event-user-section-title">Team members</h3>
            <button
              type="button"
              className="btn btn-save event-user-action-btn event-user-add-btn"
              onClick={() => {
                setShowAddForm((v) => !v);
                setFormData(emptyForm);
              }}
            >
              {showAddForm ? "Close" : "Add User"}
            </button>
          </div>
          <div className="event-user-member-list__body">
            {loading ? (
              <p className="event-user-empty">Loading…</p>
            ) : filteredUsers.length ? (
              filteredUsers.map((row) => {
                const isSelected = String(row._id) === String(selectedUserId);
                return (
                  <button
                    key={String(row._id)}
                    type="button"
                    className={`event-user-member-card${
                      isSelected ? " is-selected" : ""
                    }`}
                    onClick={() => setSelectedUserId(row._id)}
                  >
                    <strong>{row.name || "—"}</strong>
                    <span>{row.email}</span>
                    <em>
                      {row.role || "Viewer"} ·{" "}
                      {row.isActive ? "Active" : "Disabled"}
                    </em>
                  </button>
                );
              })
            ) : (
              <p className="event-user-empty">No team members yet.</p>
            )}
          </div>
        </aside>

        <section className="event-user-access-panel">
          <header className="event-user-panel-head">
            <div>
              <h3 className="event-inner-hed mb-0">
                {selectedUser
                  ? selectedUser.name || selectedUser.email
                  : "Page permissions"}
              </h3>
              <p className="event-user-panel-sub">
                {selectedUser
                  ? selectedUser.email
                  : "Select a team member to configure access"}
              </p>
            </div>
            <div className="event-user-panel-actions">
              {selectedUser && (
                <button
                  type="button"
                  className={`btn event-user-action-btn ${
                    selectedUser.isActive ? "deactivate-btn" : "btn-enable"
                  }`}
                  onClick={() => handleActivateDeactivate(selectedUser)}
                >
                  {selectedUser.isActive ? "Disable" : "Enable"}
                </button>
              )}
              <label className="event-user-role-label">
                Role preset
                <select
                  className="form-select"
                  value={draftRole}
                  disabled={!selectedUser}
                  onChange={(e) => applyRolePreset(e.target.value)}
                >
                  {EVENT_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="btn btn-save event-user-action-btn"
                disabled={saving || !selectedUser}
                onClick={handleSaveAccess}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </header>

          <div className="event-user-table-scroll">
            <table className="table event-user-perm-table mb-0">
              <thead>
                <tr>
                  <th>Page</th>
                  <th className="text-center">View</th>
                  <th className="text-center">Edit</th>
                  <th className="text-center">Delete</th>
                  <th className="text-center">Special actions</th>
                </tr>
              </thead>
              <tbody>
                {!selectedUser ? (
                  <tr>
                    <td colSpan="5" className="text-center text-muted py-4">
                      Select a team member on the left.
                    </td>
                  </tr>
                ) : (
                  permissionGroups.map((group) => (
                    <React.Fragment key={group.group}>
                      <tr className="event-user-group-row">
                        <td colSpan="5">{group.group}</td>
                      </tr>
                      {group.items.map((module) => {
                        const perm = module.perm;
                        const locked = Boolean(module.alwaysOn);
                        const specialLabel = PAGE_SPECIAL_ACTIONS[module.key];
                        return (
                          <tr key={module.key}>
                            <td>
                              <span className="event-user-page-name">
                                {module.label}
                              </span>
                              {locked ? (
                                <span className="event-user-locked">
                                  Always on
                                </span>
                              ) : null}
                            </td>
                            {["view", "edit", "delete"].map((flag) => (
                              <td key={flag} className="text-center">
                                <input
                                  type="checkbox"
                                  className="event-user-checkbox"
                                  checked={Boolean(perm[flag])}
                                  disabled={
                                    locked ||
                                    (flag !== "view" && !perm.view) ||
                                    (locked && flag !== "view")
                                  }
                                  onChange={() => togglePerm(module.key, flag)}
                                  aria-label={`${module.label} ${flag}`}
                                />
                              </td>
                            ))}
                            <td className="text-center">
                              {specialLabel ? (
                                <label className="event-user-special-cell">
                                  <input
                                    type="checkbox"
                                    className="event-user-checkbox"
                                    checked={Boolean(perm.specialActions)}
                                    disabled={locked || !perm.view}
                                    onChange={() =>
                                      togglePerm(module.key, "specialActions")
                                    }
                                    aria-label={`${module.label} special actions`}
                                  />
                                  <span>{specialLabel}</span>
                                </label>
                              ) : (
                                <span className="event-user-na">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {confirmation && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Action</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => confirmActivateDeactivate(false)}
                />
              </div>
              <div className="modal-body">
                <p>{confirmation.message}</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-cancel event-user-action-btn"
                  onClick={() => confirmActivateDeactivate(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`btn event-user-action-btn ${
                    confirmation.newStatus ? "btn-enable" : "deactivate-btn"
                  }`}
                  onClick={() => confirmActivateDeactivate(true)}
                >
                  {confirmation.newStatus ? "Enable" : "Disable"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventUser;
